import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import {
  CARE_JOURNAL_COPY,
  FORMS_COPY,
  JOURNAL_TEMPLATES,
  ROLES,
  findJournalTemplate,
  journalUnits,
  type JournalTemplateCode,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  FieldGroup,
  SectionTitle,
  Spinner,
  Textarea,
} from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { addDays, formatDate, mondayOf, todayISO } from '@/lib/format';
import { useRole } from '@/store/auth';
import type { AiWriterStatus, ClassRoomSummary, LanguageWarningRef } from '@/types';

/** 편집 상태 — per 필드는 단위 맵(요일·주차·월), 나머지는 문자열 */
type JournalContentState = Record<string, string | Record<string, string>>;

/** 서버 응답 — types.ts의 CareJournal(옛 자유 서식)과 달리 양식 필드가 있다 */
interface JournalEntry {
  id: string;
  journalDate: string;
  templateCode: string | null;
  content: JournalContentState | null;
  approvedAt: string | null;
  classRoom: { id: string; name: string };
}

interface AiJournalDraft {
  content: JournalContentState;
  observationCount: number;
  warnings: LanguageWarningRef[];
}

function hasInput(content: JournalContentState): boolean {
  return Object.values(content).some((value) =>
    typeof value === 'string'
      ? value.trim().length > 0
      : Object.values(value).some((day) => day.trim().length > 0),
  );
}

/** 주간 양식의 날짜 표시 — 주 시작(월요일) 기준 */
function weekLabel(monday: string): string {
  return `${Number(monday.slice(5, 7))}월 ${Number(monday.slice(8, 10))}일 주`;
}

/** 학년도 — 3월에 시작한다. 1·2월은 전년도 학년도에 속한다 */
function schoolYearOf(date: string): number {
  const year = Number(date.slice(0, 4));
  return Number(date.slice(5, 7)) >= 3 ? year : year - 1;
}

/** 달 단위 이동 — 'YYYY-MM-01' 기준 */
function shiftMonth(firstOfMonth: string, diff: number): string {
  const year = Number(firstOfMonth.slice(0, 4));
  const month = Number(firstOfMonth.slice(5, 7)) - 1 + diff;
  const shifted = new Date(Date.UTC(year, month, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

/**
 * 보육일지 — 양식 기반 작성 (§5.14, v1.19)
 *
 * (반, 날짜, 양식) 한 장. 주간 양식은 월요일이 journalDate가 된다.
 * AI 초안은 그 기간의 관찰메모만 근거로 하며, 교사가 확인 후 저장한다.
 */
export function CareJournalPage() {
  const queryClient = useQueryClient();
  const isDirector = useRole() === ROLES.DIRECTOR;
  const [searchParams] = useSearchParams();

  const [classRoomId, setClassRoomId] = useState('');
  const [templateCode, setTemplateCode] = useState<JournalTemplateCode>(() => {
    return findJournalTemplate(searchParams.get('template') ?? '')?.code ?? 'DAILY_JOURNAL';
  });
  // 날짜 앵커 — 주간 양식이면 그 주의 월요일로 정규화해 journalDate로 쓴다
  const [anchorDate, setAnchorDate] = useState(todayISO());

  const template = findJournalTemplate(templateCode) ?? JOURNAL_TEMPLATES[0];
  const span = template.span;
  // 저장 기준일 — 주간=월요일, 월간=1일, 연간=학년도 시작(3월 1일)
  const journalDate =
    span === 'WEEK'
      ? mondayOf(anchorDate)
      : span === 'MONTH'
        ? `${anchorDate.slice(0, 7)}-01`
        : span === 'YEAR'
          ? `${schoolYearOf(anchorDate)}-03-01`
          : anchorDate;
  const rangeEnd =
    span === 'WEEK'
      ? addDays(journalDate, 4)
      : span === 'MONTH'
        ? addDays(shiftMonth(journalDate, 1), -1)
        : span === 'YEAR'
          ? addDays(`${schoolYearOf(anchorDate) + 1}-03-01`, -1)
          : journalDate;

  const rooms = useQuery({
    queryKey: ['classrooms'],
    queryFn: () => apiData<ClassRoomSummary[]>('/classrooms'),
  });
  const effectiveRoomId = classRoomId || rooms.data?.[0]?.id || '';

  const journals = useQuery({
    queryKey: ['care-journals', { classRoomId: effectiveRoomId, from: journalDate, to: rangeEnd }],
    queryFn: () =>
      apiData<JournalEntry[]>(
        `/care-journals?classRoomId=${effectiveRoomId}&from=${journalDate}&to=${rangeEnd}`,
      ),
    enabled: Boolean(effectiveRoomId),
    placeholderData: keepPreviousData, // 반·날짜·양식 전환 시 이전 화면 유지
  });

  // placeholder(이전 조합) 데이터로는 기존 일지를 판정하지 않는다
  const loaded = journals.isSuccess && !journals.isPlaceholderData;
  const existing = loaded
    ? journals.data.find((j) => j.journalDate === journalDate && j.templateCode === templateCode)
    : undefined;

  const [content, setContent] = useState<JournalContentState>({});
  const [loadedKey, setLoadedKey] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aiInfo, setAiInfo] = useState<string | null>(null);
  const [aiWarnings, setAiWarnings] = useState<LanguageWarningRef[]>([]);

  // 조합이 바뀌면 그 조합의 저장분으로 폼을 재설정한다 — 입력이 다른 조합에 섞이지 않게
  const comboKey = `${effectiveRoomId}|${templateCode}|${journalDate}`;
  useEffect(() => {
    if (!loaded || loadedKey === comboKey) return;
    setContent(existing?.content ?? {});
    setLoadedKey(comboKey);
    setMessage(null);
    setError(null);
    setAiInfo(null);
    setAiWarnings([]);
  }, [loaded, loadedKey, comboKey, existing]);

  const aiStatus = useQuery({
    queryKey: ['ai', 'writer', 'status'],
    queryFn: () => apiData<AiWriterStatus>('/ai/writer/status'),
  });

  const aiDraft = useMutation({
    mutationFn: () =>
      apiData<AiJournalDraft>('/ai/writer/care-journal', {
        method: 'POST',
        body: { classRoomId: effectiveRoomId, templateCode, date: journalDate },
      }),
    onSuccess: (result) => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['ai', 'writer', 'status'] });
      if (result.observationCount === 0) {
        setAiInfo(CARE_JOURNAL_COPY.noObservations);
        return;
      }
      // 초안은 전체 교체 — 작성 중인 내용이 있으면 먼저 물어본다
      if (hasInput(content) && !window.confirm('작성 중인 내용을 AI 초안으로 바꿀까요?')) return;
      setContent(result.content);
      setAiInfo(null);
      setAiWarnings(result.warnings ?? []);
      setMessage(null);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'AI 초안을 만들지 못했어요.'),
  });

  const save = useMutation({
    mutationFn: () =>
      apiData<JournalEntry>('/care-journals', {
        method: 'POST',
        body: { classRoomId: effectiveRoomId, journalDate, templateCode, content },
      }),
    onSuccess: () => {
      setMessage('저장했어요.');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['care-journals'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '저장하지 못했어요.'),
  });

  const approve = useMutation({
    mutationFn: (id: string) => apiData(`/care-journals/${id}/approve`, { method: 'POST' }),
    onSuccess: () => {
      setMessage('결재했어요.');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['care-journals'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '결재하지 못했어요.'),
  });

  const fieldValue = (key: string): string => {
    const value = content[key];
    return typeof value === 'string' ? value : '';
  };
  const dayValue = (key: string, day: string): string => {
    const value = content[key];
    return typeof value === 'object' && value !== null ? (value[day] ?? '') : '';
  };
  const setField = (key: string, next: string) => {
    setContent((prev) => ({ ...prev, [key]: next }));
    setMessage(null);
  };
  const setDayField = (key: string, day: string, next: string) => {
    setContent((prev) => {
      const current = prev[key];
      const map = typeof current === 'object' && current !== null ? current : {};
      return { ...prev, [key]: { ...map, [day]: next } };
    });
    setMessage(null);
  };

  const shift = (dir: number) =>
    setAnchorDate((prev) => {
      if (span === 'WEEK') return addDays(mondayOf(prev), dir * 7);
      if (span === 'MONTH') return shiftMonth(`${prev.slice(0, 7)}-01`, dir);
      if (span === 'YEAR') return `${schoolYearOf(prev) + dir}-03-01`;
      return addDays(prev, dir);
    });

  const formsLink = (
    <Link
      to="/forms"
      className="tap flex items-center px-2 text-sm font-medium text-primary-strong"
    >
      {FORMS_COPY.title}
    </Link>
  );

  if (rooms.isPending) {
    return (
      <Screen>
        <AppBar title={CARE_JOURNAL_COPY.title} back right={formsLink} />
        <Spinner />
      </Screen>
    );
  }
  if (rooms.isError) {
    return (
      <Screen>
        <AppBar title={CARE_JOURNAL_COPY.title} back right={formsLink} />
        <ErrorNote message="반 정보를 불러오지 못했어요." onRetry={() => void rooms.refetch()} />
      </Screen>
    );
  }
  if (rooms.data.length === 0) {
    return (
      <Screen>
        <AppBar title={CARE_JOURNAL_COPY.title} back right={formsLink} />
        <EmptyState title="아직 반이 없어요" body="운영 화면에서 반을 만들면 보육일지를 쓸 수 있어요." />
      </Screen>
    );
  }

  return (
    <Screen>
      <AppBar title={CARE_JOURNAL_COPY.title} back right={formsLink} />

      <div className="flex flex-col gap-4 p-4">
        <Card className="flex flex-col gap-4">
          <FieldGroup label="반" required>
            <div className="flex flex-wrap gap-1.5">
              {rooms.data.map((room) => (
                <Chip
                  key={room.id}
                  tone={room.id === effectiveRoomId ? 'primary' : 'neutral'}
                  selected={room.id === effectiveRoomId}
                  onClick={() => setClassRoomId(room.id)}
                >
                  {room.name}
                </Chip>
              ))}
            </div>
          </FieldGroup>

          <FieldGroup label={CARE_JOURNAL_COPY.templatePick}>
            <div className="scroll-x -mx-1">
              <div className="flex w-max gap-1.5 px-1 py-1">
                {JOURNAL_TEMPLATES.map((entry) => (
                  <Chip
                    key={entry.code}
                    tone={entry.code === templateCode ? 'primary' : 'neutral'}
                    selected={entry.code === templateCode}
                    onClick={() => setTemplateCode(entry.code)}
                  >
                    {entry.name}
                  </Chip>
                ))}
              </div>
            </div>
          </FieldGroup>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => shift(-1)}
              aria-label={span === 'DAY' ? '전날' : '이전'}
              className="tap p-2"
            >
              <ChevronLeft size={20} />
            </button>
            <p className="text-base font-bold">
              {span === 'WEEK'
                ? weekLabel(journalDate)
                : span === 'MONTH'
                  ? `${Number(journalDate.slice(0, 4))}년 ${Number(journalDate.slice(5, 7))}월`
                  : span === 'YEAR'
                    ? `${schoolYearOf(journalDate)}학년도 (3월~2월)`
                    : formatDate(journalDate)}
            </p>
            <button
              type="button"
              onClick={() => shift(1)}
              aria-label={span === 'DAY' ? '다음날' : '다음'}
              className="tap p-2"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {existing?.approvedAt && (
            <Chip tone="primary" className="self-start">
              <Check size={13} /> {CARE_JOURNAL_COPY.approved}
            </Chip>
          )}
        </Card>

        {journals.isError && (
          <ErrorNote message="보육일지를 불러오지 못했어요." onRetry={() => void journals.refetch()} />
        )}

        {template.ai && aiStatus.data?.available && (
          <Card className="flex flex-col gap-2">
            <Button
              variant="outline"
              icon={<Sparkles size={16} />}
              loading={aiDraft.isPending}
              disabled={!effectiveRoomId}
              onClick={() => aiDraft.mutate()}
            >
              {CARE_JOURNAL_COPY.aiDraft}
            </Button>
            <p className="text-sm text-ink-sub">{CARE_JOURNAL_COPY.aiNotice}</p>
            {aiInfo && <p className="rounded-sm bg-info-soft px-3 py-2 text-sm">{aiInfo}</p>}
            {aiWarnings.length > 0 && (
              <div className="rounded-sm bg-info-soft px-3 py-2 text-sm">
                <p className="mb-1 font-semibold">살펴볼 표현이 있어요</p>
                <ul className="flex flex-col gap-1">
                  {aiWarnings.map((warning, index) => (
                    <li key={`${warning.term}-${index}`}>
                      <b>“{warning.term}”</b> — {warning.reason}
                      {warning.suggestions.length > 0 && (
                        <span className="text-ink-sub"> · 제안: {warning.suggestions.join(' / ')}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        )}

        {journals.isPending && Boolean(effectiveRoomId) ? (
          <Spinner />
        ) : (
          <>
            {template.sections.map((section) => (
              <section key={section.title}>
                <SectionTitle>{section.title}</SectionTitle>
                <Card className="flex flex-col gap-4">
                  {section.fields.map((field) =>
                    field.per ? (
                      <FieldGroup key={field.key} label={field.label} hint={field.hint}>
                        <div className="flex flex-col gap-2">
                          {journalUnits(field.per).map((unit) => (
                            <div key={unit.key}>
                              <p className="mb-1 text-sm font-bold text-ink-sub">{unit.label}</p>
                              <Textarea
                                rows={field.rows ?? 3}
                                value={dayValue(field.key, unit.key)}
                                onChange={(e) => setDayField(field.key, unit.key, e.target.value)}
                              />
                            </div>
                          ))}
                        </div>
                      </FieldGroup>
                    ) : (
                      <Field key={field.key} label={field.label} hint={field.hint}>
                        <Textarea
                          rows={field.rows ?? 3}
                          value={fieldValue(field.key)}
                          onChange={(e) => setField(field.key, e.target.value)}
                        />
                      </Field>
                    ),
                  )}
                </Card>
              </section>
            ))}

            {message && (
              <p className="rounded-sm bg-primary-soft px-3 py-2 text-sm text-primary-strong">
                {message}
              </p>
            )}
            {error && (
              <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
                {error}
              </p>
            )}

            <div className="flex gap-2 pb-6">
              {isDirector && existing && !existing.approvedAt && (
                <Button
                  variant="outline"
                  block
                  loading={approve.isPending}
                  onClick={() => approve.mutate(existing.id)}
                >
                  결재
                </Button>
              )}
              <Button
                block
                loading={save.isPending}
                disabled={!effectiveRoomId || !hasInput(content)}
                onClick={() => save.mutate()}
              >
                저장하기
              </Button>
            </div>
          </>
        )}
      </div>
    </Screen>
  );
}
